import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'

/**
 * Checks if the HTML contains a table element
 */
function containsTable(html: string): boolean {
  return /<table[\s>]/i.test(html)
}

/**
 * Checks if the HTML contains an iframe element
 */
function containsIframe(html: string): boolean {
  return /<iframe[\s>]/i.test(html)
}

/**
 * Extracts iframe attributes from HTML string
 */
function extractIframeAttributes(html: string): Record<string, string> | null {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const iframe = doc.querySelector('iframe')

  if (!iframe) return null

  const attributes: Record<string, string> = {}
  for (const attr of iframe.getAttributeNames()) {
    const value = iframe.getAttribute(attr)
    if (value !== null) {
      attributes[attr] = value
    }
  }

  return attributes
}

/**
 * Checks if text is a Flourish, Datawrapper, or similar embed URL
 */
function isEmbedUrl(text: string): boolean {
  const embedPatterns = [
    /flourish\.studio/i,
    /datawrapper\.dwcdn\.net/i,
    /infogram\.com/i,
    /public\.tableau\.com/i,
    /plotly\.com/i,
    /app\.powerbi\.com/i,
  ]
  return embedPatterns.some((pattern) => pattern.test(text))
}

/**
 * Parses HTML table string and converts it to TipTap table JSON format
 */
function parseHtmlTableToJson(html: string): Record<string, unknown> | null {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const table = doc.querySelector('table')

  if (!table) return null

  const rows: Record<string, unknown>[] = []
  const tableRows = table.querySelectorAll('tr')

  tableRows.forEach((tr, rowIndex) => {
    const cells: Record<string, unknown>[] = []
    const tableCells = tr.querySelectorAll('td, th')

    tableCells.forEach((cell) => {
      const isHeader = cell.tagName.toLowerCase() === 'th'
      const cellText = cell.textContent?.trim() || ''

      // Get colspan and rowspan attributes
      const colspan = parseInt(cell.getAttribute('colspan') || '1', 10)
      const rowspan = parseInt(cell.getAttribute('rowspan') || '1', 10)

      const cellNode: Record<string, unknown> = {
        type: isHeader ? 'tableHeader' : 'tableCell',
        content: [
          {
            type: 'paragraph',
            content: cellText ? [{ type: 'text', text: cellText }] : [],
          },
        ],
      }

      // Add attrs if colspan or rowspan is not 1
      if (colspan !== 1 || rowspan !== 1) {
        cellNode.attrs = {
          colspan,
          rowspan,
        }
      }

      cells.push(cellNode)
    })

    if (cells.length > 0) {
      rows.push({
        type: 'tableRow',
        content: cells,
      })
    }
  })

  if (rows.length === 0) return null

  return {
    type: 'table',
    content: rows,
  }
}

export const PasteHandler = Extension.create({
  name: 'pasteHandler',

  addProseMirrorPlugins() {
    const { editor } = this

    return [
      new Plugin({
        key: new PluginKey('pasteHandler'),
        props: {
          handlePaste: (view, event, slice) => {
            const clipboardData = event.clipboardData
            if (!clipboardData) return false

            const html = clipboardData.getData('text/html')
            const text = clipboardData.getData('text/plain')

            // Handle iframe paste (from HTML)
            if (html && containsIframe(html)) {
              const attrs = extractIframeAttributes(html)
              if (attrs && attrs.src) {
                event.preventDefault()
                editor.chain().focus().setIframe({ src: attrs.src }).run()
                return true
              }
            }

            // Handle iframe embed code paste (as plain text containing <iframe>)
            if (text && containsIframe(text)) {
              const attrs = extractIframeAttributes(text)
              if (attrs && attrs.src) {
                event.preventDefault()
                editor.chain().focus().setIframe({ src: attrs.src }).run()
                return true
              }
            }

            // Handle embed URL paste (Flourish, Datawrapper, etc.)
            if (text && isEmbedUrl(text) && !html) {
              event.preventDefault()
              editor.chain().focus().setIframe({ src: text.trim() }).run()
              return true
            }

            // Handle plain text that contains HTML table markup (copy-pasted HTML code as text)
            // Check this FIRST before checking html, because pasting HTML code as text
            // will have the raw tags in text but might also have html content
            if (text && containsTable(text)) {
              // Check if the text itself contains raw HTML tags (not rendered)
              // This happens when someone copies HTML source code
              const hasRawHtmlTags = /<\/?(?:table|tr|td|th|thead|tbody)[\s>]/i.test(text)

              if (hasRawHtmlTags) {
                event.preventDefault()

                try {
                  const tableJson = parseHtmlTableToJson(text)
                  if (tableJson) {
                    editor.chain().focus().insertContent(tableJson).run()
                    return true
                  }
                } catch (error) {
                  console.error('Error parsing table from text:', error)
                }
                return false
              }
            }

            // Handle HTML table paste (from clipboard HTML - e.g., copying from a webpage)
            if (html && containsTable(html)) {
              event.preventDefault()

              try {
                const tableJson = parseHtmlTableToJson(html)
                if (tableJson) {
                  editor.chain().focus().insertContent(tableJson).run()
                  return true
                }
              } catch (error) {
                console.error('Error parsing table from HTML:', error)
              }
              return false
            }

            // Handle plain text table paste (tab-separated or similar)
            if (text && !html && isTabularData(text)) {
              event.preventDefault()
              const tableJson = convertTextToTable(text)
              if (tableJson) {
                editor.chain().focus().insertContent(tableJson).run()
                return true
              }
            }

            return false
          },
        },
      }),
    ]
  },
})

/**
 * Checks if text appears to be tabular data (tab or comma separated)
 */
function isTabularData(text: string): boolean {
  const lines = text.trim().split('\n')
  if (lines.length < 2) return false

  // Check if lines have consistent tab or comma separators
  const firstLineTabs = (lines[0].match(/\t/g) || []).length
  const firstLineCommas = (lines[0].match(/,/g) || []).length

  if (firstLineTabs === 0 && firstLineCommas === 0) return false

  // Check consistency across lines
  const separator = firstLineTabs > 0 ? '\t' : ','
  const expectedColumns = lines[0].split(separator).length

  return lines.every((line) => {
    const columns = line.split(separator).length
    return columns === expectedColumns || columns === expectedColumns - 1
  })
}

/**
 * Converts tab/comma separated text to table JSON
 */
function convertTextToTable(text: string): Record<string, unknown> | null {
  const lines = text.trim().split('\n')
  if (lines.length < 1) return null

  const firstLineTabs = (lines[0].match(/\t/g) || []).length
  const separator = firstLineTabs > 0 ? '\t' : ','

  const rows = lines.map((line, rowIndex) => {
    const cells = line.split(separator)
    const cellType = rowIndex === 0 ? 'tableHeader' : 'tableCell'

    return {
      type: 'tableRow',
      content: cells.map((cellText) => ({
        type: cellType,
        content: [
          {
            type: 'paragraph',
            content: cellText.trim() ? [{ type: 'text', text: cellText.trim() }] : [],
          },
        ],
      })),
    }
  })

  return {
    type: 'table',
    content: rows,
  }
}

export default PasteHandler
